import { css, cx } from "@emotion/css";
import { HandWaveIcon, ExclamationCircleIcon, ExclamationTriangleIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { LicenseStatusResource } from "@octopusdeploy/octopus-server-client";
import { LicenseMessageDisposition, LicenseMessagePolicy } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import pluralize from "pluralize";
import { useAnalyticLicenseUsageDispatch } from "~/analytics/Analytics";
import { useSpaceAwareNavigation } from "../Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
export type LicenseMessageUrgency = "info" | "attention" | "danger";
export function GetLicenseExpirationUrgency(licenseDisposition: LicenseMessageDisposition | undefined): LicenseMessageUrgency {
    switch (licenseDisposition) {
        case LicenseMessageDisposition.Information:
            return "info";
        // trial expires soon
        case LicenseMessageDisposition.Notice:
            return "attention";
        // trial expires today (if remainingDays is 0) or in grace period
        case LicenseMessageDisposition.Warning:
        case LicenseMessageDisposition.Error:
            return "danger";
        default:
            return "info";
    }
}
export function LicenseCountdownBanner({ licenseStatus }: {
    licenseStatus: LicenseStatusResource;
}) {
    const licenseDisposition = licenseStatus.Messages.find((x) => x.MessagePolicy === LicenseMessagePolicy.LicensePeriodPolicy)?.Disposition;
    const bannerState = GetLicenseExpirationUrgency(licenseDisposition);
    const dispatchAction = useAnalyticLicenseUsageDispatch();
    const navigation = useSpaceAwareNavigation();
    const onBannerClicked = () => {
        dispatchAction("Click Countdown Banner", { urgency: bannerState, resource: "License" });
        navigation.navigate(links.licenseUsageDashboardPage.generateUrl());
    };
    const bannerIcon = () => {
        switch (bannerState) {
            case "info":
                return <HandWaveIcon size={20}/>;
            case "attention":
                return <ExclamationCircleIcon size={20}/>;
            case "danger":
                return <ExclamationTriangleIcon size={20}/>;
            default:
                return <HandWaveIcon size={20}/>;
        }
    };
    return (<div className={cx(styles.bannerContainer, styles.bannerState[bannerState])} onClick={onBannerClicked}>
            {bannerIcon()}

            {/* license is not in grace period and not expired */}
            {licenseStatus.DaysToEffectiveExpiryDate >= 0 ? (<div>
                    {/* trial expires today (if remainingDays is 0) */}
                    {pluralize("day", licenseStatus.DaysToEffectiveExpiryDate + 1, true)} left in Enterprise trial
                </div>) : (<div>Enterprise trial has ended</div>)}
            <div className={styles.licenseUsage}>View License Usage</div>
        </div>);
}
const styles = {
    bannerContainer: css({
        minHeight: "2.25rem",
        display: "flex",
        flexWrap: "wrap",
        padding: `${space[8]} ${space[12]}`,
        justifyContent: "center",
        gap: space[6],
        borderRadius: borderRadius["small"],
        cursor: "pointer",
        fontSize: fontSize["medium"],
        "& b": {
            fontWeight: fontWeight["700"],
        },
    }),
    bannerState: {
        info: css({
            backgroundColor: themeTokens.color.callout.background.info.default,
            "& svg": {
                fill: themeTokens.color.callout.icon.info,
            },
            "&:hover": {
                backgroundColor: themeTokens.color.callout.background.info.hover,
            },
            "&:active": {
                backgroundColor: themeTokens.color.callout.background.info.pressed,
            },
        }),
        attention: css({
            backgroundColor: themeTokens.color.callout.background.warning.default,
            "& svg": {
                fill: themeTokens.color.callout.icon.warning,
            },
            "&:hover": {
                backgroundColor: themeTokens.color.callout.background.warning.hover,
            },
            "&:active": {
                backgroundColor: themeTokens.color.callout.background.warning.pressed,
            },
        }),
        danger: css({
            backgroundColor: themeTokens.color.callout.background.danger.default,
            "& svg": {
                fill: themeTokens.color.callout.icon.danger,
            },
            "&:hover": {
                backgroundColor: themeTokens.color.callout.background.danger.hover,
            },
            "&:active": {
                backgroundColor: themeTokens.color.callout.background.danger.pressed,
            },
        }),
    },
    licenseUsage: css({
        color: themeTokens.color.text.link.default,
        fontSize: fontSize["medium"],
        fontWeight: fontWeight["700"],
    }),
};
